export enum PartyType {
  NoPreference = "none",
  Plaintiff = "plaintiff",
  Defendant = "defendant",
}

export enum SubmissionFulfillmentType {
  Standard = "automated",
  WhiteGloveSearch = "custom",
  RecordAnalysis = "case-analysis",
}

export enum SubmissionStatus {
  Draft = "draft",
  PendingPayment = "pending_payment",
  PendingReview = "pending_review",
  Soliciting = "soliciting",
  Active = "active",
  Fulfilled = "fulfilled",
  Expired = "expired",
  Cancelled = "cancelled",
}

export enum ManagedCaseStatus {
  IntakePending = "intake_pending",
  IntakeComplete = "intake_complete",
  CaseManagerAssigned = "case_manager_assigned",
  Searching = "searching",
  PhysicianAnalystAssigned = "physician_analyst_assigned",
  InQueue = "in_queue",
  Reviewing = "reviewing",
  ClientNotified = "client_notified",
  ExpertSelected = "expert_selected",
  HandoffComplete = "handoff_complete",
  FollowUpScheduled = "follow_up_scheduled",
  CaseComplete = "case_complete",
}

export const ManagedCaseStatusByType: Record<string, ManagedCaseStatus[]> = {
  [SubmissionFulfillmentType.WhiteGloveSearch]: [
    ManagedCaseStatus.IntakePending,
    ManagedCaseStatus.IntakeComplete,
    ManagedCaseStatus.CaseManagerAssigned,
    ManagedCaseStatus.Searching,
    ManagedCaseStatus.ClientNotified,
    ManagedCaseStatus.ExpertSelected,
    ManagedCaseStatus.HandoffComplete,
  ],
  [SubmissionFulfillmentType.RecordAnalysis]: [
    ManagedCaseStatus.IntakePending,
    ManagedCaseStatus.IntakeComplete,
    ManagedCaseStatus.CaseManagerAssigned,
    ManagedCaseStatus.PhysicianAnalystAssigned,
    ManagedCaseStatus.InQueue,
    ManagedCaseStatus.Reviewing,
    ManagedCaseStatus.ClientNotified,
    ManagedCaseStatus.FollowUpScheduled,
    ManagedCaseStatus.CaseComplete,
  ],
};

export enum ExpertStatus {
  New = "new",
  PendingReview = "pending",
  Active = "active",
  Denied = "denied",
  Closed = "closed",
  Spam = "spam",
}

export enum ExpertAcceptanceStatus {
  Pending = "pending",
  Solicited = "solicited",
  ExpertAccepted = "accepted",
  ExpertAcceptedLate = "accepted-late",
  ExpertDeclined = "declined",
  Expired = "expired",
}

// Mirrors app/Enums/ClientAcceptanceStatus.php in the API, which backs a real
// MySQL ENUM column -- these strings must match its `value`s character for
// character or a save is rejected by the database, not by validation.
// Remapped 5 -> 4 by plan 0010: pending+reviewing -> reviewing (a merge),
// accepted -> contacted, declined -> passed, hired -> retained.
// `models/ExpertMatch.ts` reads this through `getAsChoices()`, so the dropdown
// and its labels follow from the case names here with no edit there.
export enum ClientAcceptanceStatus {
  Reviewing = "reviewing",
  Contacted = "contacted",
  Retained = "retained",
  Passed = "passed",
}

// Mirrors app/Enums/ClientCaseState.php in the API -- the attorney-facing
// projection of a managed case's status (plan 20260810-1703-attorney-portal
// Task 3). These string values must match the PHP enum's `value`s character for
// character.
//
// Do NOT render these through getAsChoices(): it derives display text from the
// enum KEY by splitting on capitals ("SearchingForExperts" -> "Searching For
// Experts"), which is Title Case, not the founder-approved sentence case. Use
// ClientCaseStateLabels below as the single source of display text.
export enum ClientCaseState {
  GettingStarted = "getting_started",
  SearchingForExperts = "searching_for_experts",
  ExpertsReady = "experts_ready",
  Complete = "complete",
}

export const ClientCaseStateLabels: Record<ClientCaseState, string> = {
  [ClientCaseState.GettingStarted]: "Getting started",
  [ClientCaseState.SearchingForExperts]: "Searching for experts",
  [ClientCaseState.ExpertsReady]: "Experts ready for you",
  [ClientCaseState.Complete]: "Complete",
};

export enum UserRole {
  Admin = "admin",
  Expert = "expert",
  Client = "client",
}

export enum CommunicationMethod {
  Phone = "phone",
  Email = "email",
  Text = "text",
  VirtualMeeting = "virtual_meeting",
}

export enum LawFirmRole {
  OwnerPartner = "owner_partner",
  Attorney = "attorney",
  Other = "other",
}

export enum PracticeArea {
  MedMal = "med_mal",
  PersonalInjury = "personal_injury",
  Other = "other",
}

export enum Urgency {
  Urgent = "urgent",
  NotUrgent = "not_urgent",
}

export enum ProductType {
  Standard = "single-automated",
  StandardPack = "pack-automated",
  WhiteGloveSearch = "custom",
  RecordAnalysis = "case-analysis",
}

export enum PaymentMethod {
  Credit = "credit",
  Invoice = "invoice",
  Comped = "comped",
}

// The four states InvoiceStatusChip renders, as filter values. Not persisted on
// the model — only the value of the "Invoice Status" My Cases column filter,
// translated to SQL by the backend Submission::filterByInvoiceStatus. Keys are
// title-cased by getAsChoices into the dropdown labels ("No Invoice", "Not
// Sent", "Awaiting Payment", "Paid"), matching the chip's wording.
export enum InvoiceStatus {
  NoInvoice = "none",
  NotSent = "not_sent",
  AwaitingPayment = "awaiting_payment",
  Paid = "paid",
}

// Where an account stands with the Membership subscription — the same four
// states the backend's MembershipStatus reports. The case names are the admin
// user list's column labels, not the backend's case names: getAsChoices
// title-cases the KEY, so "Member" and "Canceling" read better in a table cell
// and a filter dropdown than "Active" and "Grace" would.
export enum MembershipStatus {
  None = "none",
  Trial = "trialing",
  Member = "active",
  Canceling = "grace",
}

export function getAsChoices<T extends Record<string, string>>(
  enumType: T
): { [key in T[keyof T]]: string } {
  return Object.keys(enumType).reduce((acc, key) => {
    const spacedTitleCase = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
    acc[enumType[key as keyof T] as T[keyof T]] = spacedTitleCase;
    return acc;
  }, {} as { [key in T[keyof T]]: string });
}
